import { defineStore } from "pinia"
import { isRxDocument, RxDocument } from "rxdb"
import { CorrectiveWorkOnItemDocument } from "src/db/collectionDefinitions/csi/correctiveWork"
import { WorkOrderRegularItemDocument } from "src/db/collectionDefinitions/csi/workOrder"
import { getCommonDB, getDB } from "src/db/databases"
import { Log } from "src/util/classes/log"
import { CACHE_AVATARS, CACHE_IMAGES } from "src/util/constants/configurations"
import {
  isAttributeChar,
  isAttributeNumeric,
  isAttributeSelect,
} from "src/util/functions/attributes"
import { Attachment, AttachmentData } from "src/util/types/dataModels/attachment"
import {
  AttributeBaseExtensions,
  AttributeChar,
  AttributeNumeric,
  AttributeSelect,
  AttributeSelectOptions,
  AttributeTemporal,
  AttributeType,
  AttributeValueCharFull,
  AttributeValueNumericFull,
  AttributeValueSelectFull,
  AttributeValueTemporalFull,
} from "src/util/types/dataModels/attribute"
import {
  IssueCardData,
  IssueEventCardCacheData,
  IssuesOnItem,
  IssuesOnItemAction,
} from "src/util/types/dataModels/issue"
import { IssueComment } from "src/util/types/dataModels/issueComment"
import { IssuePriority } from "src/util/types/dataModels/issuePriority"
import { Item, ItemCardData } from "src/util/types/dataModels/item"
import {
  ItemClass,
  ItemClassPromotedAttribute,
  ItemClassWithAttributes,
} from "src/util/types/dataModels/itemClass"
import { ItemModel } from "src/util/types/dataModels/itemModel"
import {
  CorrectiveWorkOnItem,
  PerformedWorkComment,
  PerformedWorkOnItemCardData,
  RegularWorkOnItem,
} from "src/util/types/dataModels/performedWork"
import { UserAvatar, UserBasic, UserRole } from "src/util/types/dataModels/user"
import { WorkDomainGlobal, WorkDomainWorkspace } from "src/util/types/dataModels/workDomain"
import {
  WorkOrderCardData,
  WorkOrderCorrectiveItem,
  WorkOrderRegularItem,
} from "src/util/types/dataModels/workOrder"
import {
  WorkOrderCorrectiveItemComment,
  WorkOrderRegularItemComment,
} from "src/util/types/dataModels/workOrderComment"
import { WorkOrderPriority } from "src/util/types/dataModels/workOrderPriority"
import { WorkspaceDetail } from "src/util/types/dataModels/workspace"

export interface CacheStoreType {
  //region Items

  item: Map<string, Item>
  item_class: Map<string, ItemClass>
  item_model: Map<string, ItemModel>

  //endregion

  //region Attributes

  attribute_char: Map<string, AttributeChar>
  attribute_numeric: Map<string, AttributeNumeric>
  attribute_select: Map<string, AttributeSelect>
  attribute_select_options: Map<string, AttributeSelectOptions>
  attribute_temporal: Map<string, AttributeTemporal>

  // The keys are attribute id + entity id
  attribute_char_value: Map<string, AttributeValueCharFull>
  attribute_numeric_value: Map<string, AttributeValueNumericFull>
  attribute_select_value: Map<string, AttributeValueSelectFull>
  attribute_temporal_value: Map<string, AttributeValueTemporalFull>

  //endregion

  //region Issues

  issue_priority: Map<string, IssuePriority>
  issues_on_item: Map<string, IssuesOnItem>
  issue_on_item_actions: Map<string, IssuesOnItemAction>
  issue_on_item_comments: Map<string, IssueComment>

  //endregion

  //region Work

  corrective_work_on_item: Map<string, CorrectiveWorkOnItem>
  regular_work_on_item: Map<string, RegularWorkOnItem>
  corrective_work_item_comments: Map<string, PerformedWorkComment>
  regular_work_item_comments: Map<string, PerformedWorkComment>

  work_domain_workspace: Map<string, WorkDomainWorkspace>

  work_order_corrective_item: Map<string, WorkOrderCorrectiveItem>
  work_order_corrective_item_comments: Map<string, WorkOrderCorrectiveItemComment>
  work_order_regular_item: Map<string, WorkOrderRegularItem>
  work_order_regular_item_comments: Map<string, WorkOrderRegularItemComment>
  work_order_priority: Map<string, WorkOrderPriority>

  //endregion

  //region Common

  work_domain_global: Map<string, WorkDomainGlobal>
  workspaces: Map<string, WorkspaceDetail>
  user: Map<string, UserBasic>
  user_avatar: Map<string, UserAvatar>
  user_roles: Map<string, UserRole>

  //endregion

  //region Others

  attachment: Map<string, Attachment>
  attachment_data: Map<string, AttachmentData>
  itemClassWithAttributes: Map<string, ItemClassWithAttributes>
  itemClassPromotedAttribute: Map<string, ItemClassPromotedAttribute>
  itemIssues: Map<string, IssuesOnItem[]>
  itemWorkOrders: Map<string, WorkOrderRegularItem[]>
  issueWithOpenCorrectiveWork: Map<string, CorrectiveWorkOnItem>

  // Identifiers

  //endregion

  //region Cards

  itemCard: Map<string, ItemCardData>
  issueCard: Map<string, IssueCardData>
  performedWorkCard: Map<string, PerformedWorkOnItemCardData>
  issueEventCards: Map<string, IssueEventCardCacheData>
  workOrderCard: Map<string, WorkOrderCardData>

  //endregion
}

export const useCacheStore = defineStore("pinia-cacheStore", {
  state: (): CacheStoreType => ({
    item: new Map(),
    item_class: new Map(),
    item_model: new Map(),
    issue_priority: new Map(),
    issues_on_item: new Map(),
    issue_on_item_actions: new Map(),
    issue_on_item_comments: new Map(),
    corrective_work_on_item: new Map(),
    regular_work_on_item: new Map(),
    corrective_work_item_comments: new Map(),
    regular_work_item_comments: new Map(),
    attachment: new Map(),
    attachment_data: new Map(),

    attribute_char: new Map(),
    attribute_numeric: new Map(),
    attribute_select: new Map(),
    attribute_select_options: new Map(),
    attribute_temporal: new Map(),

    attribute_char_value: new Map(),
    attribute_numeric_value: new Map(),
    attribute_select_value: new Map(),
    attribute_temporal_value: new Map(),

    itemCard: new Map(),
    issueCard: new Map(),
    performedWorkCard: new Map(),
    issueEventCards: new Map(),
    workOrderCard: new Map(),

    itemClassWithAttributes: new Map(),
    itemClassPromotedAttribute: new Map(),
    itemIssues: new Map(),
    itemWorkOrders: new Map(),
    issueWithOpenCorrectiveWork: new Map(),

    work_order_corrective_item: new Map(),
    work_order_corrective_item_comments: new Map(),
    work_order_regular_item: new Map(),
    work_order_regular_item_comments: new Map(),
    work_order_priority: new Map(),

    work_domain_workspace: new Map(),
    work_domain_global: new Map(),
    workspaces: new Map(),
    user: new Map(),
    user_avatar: new Map(),
    user_roles: new Map(),
  }),

  getters: {
    getCollection: (state) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return (collection: string): Map<string, unknown> => state[collection]
    },
  },

  actions: {
    async rebuildCache() {
      console.debug("Building caches")

      this.$reset()
      await caches.delete(CACHE_IMAGES)
      await caches.delete(CACHE_AVATARS)

      const timerText = "Cache Build Time"

      console.time(timerText)

      try {
        // TODO: Make generic function to deal with this mess
        const db = await getDB()

        const itemData = await db.item.find().exec()
        this.item = dbToMap<Item>(itemData)

        const itemClassData = await db.item_class.find().exec()
        this.item_class = dbToMap<ItemClass>(itemClassData)

        const attributeCharData = await db.attribute_char.find().exec()
        this.attribute_char = dbToMap<AttributeChar>(attributeCharData)

        const attributeNumericData = await db.attribute_numeric.find().exec()
        this.attribute_numeric = dbToMap<AttributeNumeric>(attributeNumericData)

        const attributeSelectData = await db.attribute_select.find().exec()
        this.attribute_select = dbToMap<AttributeSelect>(attributeSelectData)

        const attributeSelectOptionsData = await db.attribute_select_options.find().exec()
        this.attribute_select_options = dbToMap<AttributeSelectOptions>(attributeSelectOptionsData)

        const attributeTemporalData = await db.attribute_temporal.find().exec()
        this.attribute_temporal = dbToMap<AttributeTemporal>(attributeTemporalData)

        const attributeCharValueData = await db.attribute_char_value.find().exec()
        this.attribute_char_value =
          dbToMapAttributes<AttributeValueCharFull>(attributeCharValueData)

        const attributeNumericValueData = await db.attribute_numeric_value.find().exec()
        this.attribute_numeric_value =
          dbToMapAttributes<AttributeValueNumericFull>(attributeNumericValueData)

        const attributeSelectValueData = await db.attribute_select_value.find().exec()
        this.attribute_select_value =
          dbToMapAttributes<AttributeValueSelectFull>(attributeSelectValueData)

        const attributeTemporalValueData = await db.attribute_temporal_value.find().exec()
        this.attribute_temporal_value = dbToMapAttributes<AttributeValueTemporalFull>(
          attributeTemporalValueData
        )

        const issuePrioritiesData = await db.issue_priority.find().exec()
        this.issue_priority = dbToMap<IssuePriority>(issuePrioritiesData)

        const issueOnItemData = await db.issues_on_item.find().exec()
        this.issues_on_item = dbToMap<IssuesOnItem>(issueOnItemData)
        cacheItemWithIssues(issueOnItemData, this.itemIssues)

        const issueOnItemActionsData = await db.issue_on_item_actions.find().exec()
        this.issue_on_item_actions = dbToMap<IssuesOnItemAction>(issueOnItemActionsData)

        const issueOnItemCommentsData = await db.issue_on_item_comments.find().exec()
        this.issue_on_item_comments = dbToMap<IssueComment>(issueOnItemCommentsData)

        const correctiveWorkOnItemData = await db.corrective_work_on_item.find().exec()
        this.corrective_work_on_item = dbToMap<CorrectiveWorkOnItem>(correctiveWorkOnItemData)
        cacheInsertIssueWithOpenCorrectiveWork(
          correctiveWorkOnItemData,
          this.issueWithOpenCorrectiveWork
        )

        const regularWorkOnItemData = await db.regular_work_on_item.find().exec()
        this.regular_work_on_item = dbToMap<RegularWorkOnItem>(regularWorkOnItemData)

        const workOrderCorrectiveItemData = await db.work_order_corrective_item.find().exec()
        this.work_order_corrective_item = dbToMap<WorkOrderCorrectiveItem>(
          workOrderCorrectiveItemData
        )

        const workOrderCorrectiveItemCommentData = await db.work_order_corrective_comments
          .find()
          .exec()
        this.work_order_corrective_item_comments = dbToMap<WorkOrderCorrectiveItemComment>(
          workOrderCorrectiveItemCommentData
        )

        const workOrderRegularItemData = await db.work_order_regular_item.find().exec()
        this.work_order_regular_item = dbToMap<WorkOrderRegularItem>(workOrderRegularItemData)
        cacheInsertItemWithWorkOrders(workOrderRegularItemData, this.itemWorkOrders)

        const workOrderRegularItemCommentData = await db.work_order_regular_comments.find().exec()
        this.work_order_regular_item_comments = dbToMap<WorkOrderRegularItemComment>(
          workOrderRegularItemCommentData
        )

        const workOrderPriorityData = await db.work_order_priority.find().exec()
        this.work_order_priority = dbToMap<WorkOrderPriority>(workOrderPriorityData)

        const attachmentsData = await db.attachment.find().exec()
        this.attachment = dbToMap<Attachment>(attachmentsData)

        const attachmentContentData = await db.attachment_data.find().exec()
        this.attachment_data = dbToMap<AttachmentData>(attachmentContentData)

        const workDomainsWorkspaceData = await db.work_domain_workspace.find().exec()
        this.work_domain_workspace = dbToMap<WorkDomainWorkspace>(workDomainsWorkspaceData)

        const commonDb = await getCommonDB()

        const workDomainsGlobalData = await commonDb.work_domain_global.find().exec()
        this.work_domain_global = dbToMap<WorkDomainGlobal>(workDomainsGlobalData)

        const workspacesData = await commonDb.workspace.find().exec()
        this.workspaces = dbToMap<WorkspaceDetail>(workspacesData)

        const userData = await commonDb.user.find().exec()
        this.user = dbToMap<UserBasic>(userData)

        const userAvatarData = await commonDb.user_avatar.find().exec()
        this.user_avatar = dbToMap<UserAvatar>(userAvatarData)

        const userRolesData = await commonDb.user_role.find().exec()
        this.user_roles = dbToMap<UserRole>(userRolesData)

        await cachePromotedAttribute()
      } catch (error) {
        Log.error({ message: "Couldn't rebuild caches", error })
      }

      console.timeEnd(timerText)
    },
  },
})

function dbToMap<ReturnType extends { id: string }>(
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  docs: RxDocument<ReturnType, any>[]
): Map<string, ReturnType> {
  return new Map(docs.map<[string, ReturnType]>((element) => [element.id, element.toMutableJSON()]))
}

type AttributeValueFull =
  | AttributeValueCharFull
  | AttributeValueNumericFull
  | AttributeValueSelectFull
  | AttributeValueTemporalFull

function dbToMapAttributes<ReturnType extends AttributeValueFull>(
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  docs: RxDocument<ReturnType, any>[]
): Map<string, ReturnType> {
  return new Map(
    docs.map<[string, ReturnType]>((element) => {
      const id = getAttributeValueIDFromValue(element)

      return [id, element.toMutableJSON()]
    })
  )
}

export function cacheItemWithIssues(issueList: IssuesOnItem[], map: Map<string, IssuesOnItem[]>) {
  for (const doc of issueList) {
    const issues = map.get(doc.actionable_item)
    if (issues) {
      // Only if already not present
      if (!issues.some((issue) => issue.id === doc.id)) issues.push(doc)
    } else {
      map.set(doc.actionable_item, [doc])
    }
  }
}

// TODO: Those ItemWithWorkOrders caching functions are just TRASH. Find a way to improve them if possible.

export function cacheInsertItemWithWorkOrders(
  workOrderList: Array<WorkOrderRegularItem | WorkOrderRegularItemDocument>,
  itemWorkOrderMap: Map<string, WorkOrderRegularItem[]>
) {
  for (const workOrder of workOrderList) {
    if (workOrder.items.length === 0) continue

    const mutableWorkOrder = isRxDocument(workOrder)
      ? (workOrder as WorkOrderRegularItemDocument).toMutableJSON()
      : workOrder

    insertItemWithWorkOrderHelper(mutableWorkOrder.items, mutableWorkOrder, itemWorkOrderMap)
  }
}

export function cacheUpdateItemWithWorkOrders(
  workOrderListOld: Array<WorkOrderRegularItem | WorkOrderRegularItemDocument>,
  workOrderListNew: Array<WorkOrderRegularItem | WorkOrderRegularItemDocument>,
  itemWorkOrderMap: Map<string, WorkOrderRegularItem[]>
) {
  if (workOrderListOld.length !== workOrderListNew.length) {
    Log.error({
      message: "Mismatch between lists when trying to update item work order map",
      data: {
        workOrderListOld,
        workOrderListNew,
        itemWorkOrderMap,
      },
    })
    return
  }

  for (const [i, workOrder] of workOrderListNew.entries()) {
    const oldItems = [...workOrderListOld[i].items].sort()
    const newItems = [...workOrderListNew[i].items].sort()

    if (oldItems.toString() === newItems.toString()) {
      // There are no changes in the item lists, so skip this one
      continue
    }

    const filteredOldItems = oldItems.filter((i) => !newItems.includes(i))
    const filteredNewItems = newItems.filter((i) => !oldItems.includes(i))

    deleteItemWithWorkOrderHelper(filteredOldItems, workOrder, itemWorkOrderMap)
    insertItemWithWorkOrderHelper(filteredNewItems, workOrder, itemWorkOrderMap)

    for (const filteredOldItem of filteredOldItems) {
      const cachedItem = itemWorkOrderMap.get(filteredOldItem)

      if (!cachedItem) continue

      const index = cachedItem.findIndex((wo) => wo.id === workOrder.id)
      if (index >= 0) {
        cachedItem.splice(index, 1)
      }
    }
  }
}

export function cacheDeleteItemWithWorkOrders(
  workOrderList: Array<WorkOrderRegularItem | WorkOrderRegularItemDocument>,
  itemWorkOrderMap: Map<string, WorkOrderRegularItem[]>
) {
  for (const workOrder of workOrderList) {
    deleteItemWithWorkOrderHelper(workOrder.items, workOrder, itemWorkOrderMap)
  }
}

function insertItemWithWorkOrderHelper(
  items: string[],
  workOrder: WorkOrderRegularItem,
  itemWorkOrderMap: Map<string, WorkOrderRegularItem[]>
) {
  for (const item of items) {
    const cachedItemWorkOrders = itemWorkOrderMap.get(item)
    if (!cachedItemWorkOrders) {
      itemWorkOrderMap.set(item, [workOrder])
    } else if (!cachedItemWorkOrders.some((wo) => wo.id === workOrder.id)) {
      cachedItemWorkOrders.push(workOrder)
    }
  }
}

function deleteItemWithWorkOrderHelper(
  items: string[],
  workOrder: WorkOrderRegularItem,
  itemWorkOrderMap: Map<string, WorkOrderRegularItem[]>
) {
  for (const item of items) {
    const cachedItem = itemWorkOrderMap.get(item)

    if (!cachedItem) continue

    const index = cachedItem.findIndex((wo) => wo.id === workOrder.id)
    if (index >= 0) {
      cachedItem.splice(index, 1)
    }
  }
}

export function getAttributeValueIDFromValue(attributeValue: AttributeValueFull) {
  return (
    attributeValue.attribute +
    (attributeValue.item ||
      attributeValue.item_model ||
      attributeValue.area ||
      attributeValue.location)
  )
}

export function getAttributeValueIDFromEntity(entityId: string, attributeId: string) {
  return attributeId + entityId
}

export function cacheInsertIssueWithOpenCorrectiveWork(
  correctiveWorkList: CorrectiveWorkOnItem[] | CorrectiveWorkOnItemDocument[],
  map: Map<string, CorrectiveWorkOnItem>
) {
  for (const work of correctiveWorkList) {
    if (work.conclusion) {
      // This shouldn't happen since open corrective work and issues should be 1 to 1, but it won't throw an error,
      // and it is better to be safe with caching.
      map.delete(work.issue)
    } else {
      const mutableWork = isRxDocument(work)
        ? (work as CorrectiveWorkOnItemDocument).toMutableJSON()
        : work
      map.set(work.issue, mutableWork)
    }
  }
}

export function cacheUpdateIssueWithOpenCorrectiveWork(
  correctiveWorkListOld: CorrectiveWorkOnItem | CorrectiveWorkOnItemDocument,
  correctiveWorkListNew: CorrectiveWorkOnItem | CorrectiveWorkOnItemDocument,
  map: Map<string, CorrectiveWorkOnItem>
) {
  // This is the only change we really care about
  if (correctiveWorkListNew.conclusion !== correctiveWorkListOld.conclusion) {
    // We cannot ever set the conclusion back to null/undefined, so we know for certain that the work is concluded
    map.delete(correctiveWorkListNew.issue)
  }
}

export function cacheDeleteIssueWithOpenCorrectiveWork(
  correctiveWorkList: CorrectiveWorkOnItem | CorrectiveWorkOnItemDocument,
  map: Map<string, CorrectiveWorkOnItem>
) {
  // Since the open corrective to issue ratio is 1 to 1, we need to only check work without a conclusions,
  // since those with conclusion wouldn't even be in the list.
  if (correctiveWorkList.conclusion === null) {
    map.delete(correctiveWorkList.issue)
  }
}

async function cachePromotedAttribute() {
  const db = await getDB()
  const itemClassesList = await db.item_class.find().exec()

  for (const itemClass of itemClassesList) {
    const attributeChar = await db.attribute_char
      .findOne()
      .where({ id: { $in: itemClass.attributes_char_item } })
      .sort({ order: "asc" })
      .exec()

    const attributeNumeric = await db.attribute_numeric
      .findOne()
      .where({ id: { $in: itemClass.attributes_numeric_item } })
      .sort({ order: "asc" })
      .exec()

    const attributeSelect = await db.attribute_select
      .findOne()
      .where({ id: { $in: itemClass.attributes_select_item } })
      .sort({ order: "asc" })
      .exec()

    const attributeTemporal = await db.attribute_temporal
      .findOne()
      .where({ id: { $in: itemClass.attributes_temporal_item } })
      .sort({ order: "asc" })
      .exec()

    const data = [
      attributeChar?.toMutableJSON(),
      attributeNumeric?.toMutableJSON(),
      attributeSelect?.toMutableJSON(),
      attributeTemporal?.toMutableJSON(),
    ]

    const promotedAttribute = data.reduce((a, b) => reducePromotedAttribute(a, b))

    if (promotedAttribute) {
      useCacheStore().itemClassPromotedAttribute.set(
        itemClass.id,
        attributeToPromotedAttribute(promotedAttribute)
      )
    }
  }
}

function reducePromotedAttribute(
  a: AttributeBaseExtensions | undefined,
  b: AttributeBaseExtensions | undefined
): AttributeBaseExtensions | undefined {
  if (a && b) {
    if (a.order === b.order) {
      return a.name.localeCompare(b.name) ? a : b
    } else {
      return a.order < b.order ? a : b
    }
  } else {
    return a
  }
}

function attributeToPromotedAttribute(
  attribute: AttributeBaseExtensions
): ItemClassPromotedAttribute {
  let attributeType: AttributeType

  if (isAttributeChar(attribute)) {
    attributeType = "char"
  } else if (isAttributeNumeric(attribute)) {
    attributeType = "numeric"
  } else if (isAttributeSelect(attribute)) {
    attributeType = "select"
  } else {
    attributeType = "temporal"
  }

  return {
    attributeType,
    attributeData: attribute,
  }
}
